import {statusElementsHolder} from "@/shared/recording/statusElementsHolder";
import {breadcrumbsWithoutName, htmlEscape} from "@/shared/utils/string";
import {BrowserStorageSettings} from "@/shared/browserStorageSettings";
import {SecurityStatus} from "@/shared/securityStatus";
import {formatDate, formatDateDay, parseISODate} from "@/shared/utils/datefunctions";
import {AbsentUser, HolidayUser, InactiveUser, NowActiveUser, NoWorkDayUser} from "@/shared/timr";
import {PresenceBoardVisibility, WorkingTimeTypeSubCategory} from "@/shared/timrTypes";

export interface PresenceBoardHelperConfig {
	displayRunningProjectTime?: boolean;
	reducedCards?: boolean;
}

interface PresenceBoardCardConfig {
	fullName?: string;
	categoryDisplay?: string;
	assignedGroups?: string;
	colorType?: PresenceState;
	categoryDisplayExtraInfo?: string;
	extraInfo?: string;
	icon?: string;
	taskName?: string;
	taskPath?: string;
	taskColor?: string;
}


/** Presence states a card can show; each maps to a `data-presence-state` value in CSS. */
export type PresenceState = "active" | "break" | "away" | "inactive" | "not-active-yet" | "absent";

export class PresenceBoardHelper {

	private static readonly EMPTY_LINE = "&#8204;";
	public static EVENT_SESSION_SETTINGS_CHANGED = "presenceBoardSessionSettingsChanged";
	public static ICON_PAUSED = "icon-paused";
	public static ICON_RECORDING = "icon-recording";

	private config: PresenceBoardHelperConfig;

	constructor(config: PresenceBoardHelperConfig) {
		this.config = config;
	}

	public static setPresenceBoardSessionSettings(presenceBoardVisibility: PresenceBoardVisibility): boolean {
		const presenceBoardSessionSettings = PresenceBoardHelper.getPresenceBoardSessionSettings(presenceBoardVisibility);
		BrowserStorageSettings.setPresenceBoardShowAllFilter(
			presenceBoardSessionSettings
		);
		statusElementsHolder.updateStatus();
		return presenceBoardSessionSettings;
	}

	public static getPresenceBoardSessionSettings(presenceBoardVisibility: PresenceBoardVisibility): boolean {
		// showPresenceBoardOfOtherUsers (true == all, false == group)
		const otherUsersSavedSetting = BrowserStorageSettings.getPresenceBoardShowAllFilter();
		if (otherUsersSavedSetting) {	// If saved setting is true && PresenceBoardVisibility.OFF, then show all users
			return presenceBoardVisibility === PresenceBoardVisibility.ALL;
		} else if (presenceBoardVisibility === PresenceBoardVisibility.GROUP) {	// force group setting
			return false;
		} else {	// PresenceBoardVisibility.OFF | PresenceBoardVisibility.ALL == use saved setting if corporate features are allowed
			return SecurityStatus.isCorporateFeaturesAllowed() ? otherUsersSavedSetting : true;
		}
	}

	public getActiveUserCard(presenceBoardUser: NowActiveUser) {
		if (this.config.reducedCards) {
			return this.getReducedPresenceBoardUserCard(this.getActiveUserConfig(presenceBoardUser));
		} else {
			return this.getPresenceBoardUserCard(this.getActiveUserConfig(presenceBoardUser));
		}
	}

	public getInactiveUserCard(presenceBoardUser: InactiveUser) {
		if (this.config.reducedCards) {
			return this.getReducedPresenceBoardUserCard(this.getInactiveUserConfig(presenceBoardUser));
		} else {
			return this.getPresenceBoardUserCard(this.getInactiveUserConfig(presenceBoardUser));
		}
	}

	public getAbsentUserCard(presenceBoardUser: AbsentUser) {
		if (this.config.reducedCards) {
			return this.getReducedPresenceBoardUserCard(this.getAbsentUserConfig(presenceBoardUser));
		} else {
			return this.getPresenceBoardUserCard(this.getAbsentUserConfig(presenceBoardUser));
		}
	}

	public getHolidayUserCard(presenceBoardUser: HolidayUser) {
		if (this.config.reducedCards) {
			return this.getReducedPresenceBoardUserCard(this.getHolidayUserConfig(presenceBoardUser));
		} else {
			return this.getPresenceBoardUserCard(this.getHolidayUserConfig(presenceBoardUser));
		}
	}

	public getNoWorkingDayUserCard(presenceBoardUser: NoWorkDayUser) {
		if (this.config.reducedCards) {
			return this.getReducedPresenceBoardUserCard(this.getNoWorkDayUserConfig(presenceBoardUser));
		} else {
			return this.getPresenceBoardUserCard(this.getNoWorkDayUserConfig(presenceBoardUser));
		}
	}

	private getActiveUserConfig(presenceBoardUser: NowActiveUser): PresenceBoardCardConfig {
		let color: PresenceState;
		if (presenceBoardUser.paused) {
			color = "break";
		} else if (
			presenceBoardUser.workingTimeTypeSubCategory === WorkingTimeTypeSubCategory.Away ||
			presenceBoardUser.workingTimeTypeSubCategory === WorkingTimeTypeSubCategory.ActiveDrivingTime ||
			presenceBoardUser.workingTimeTypeSubCategory === WorkingTimeTypeSubCategory.PassiveTravellingTime
		) {
			color = "away";
		} else {
			color = "active";
		}

		let taskColor: string = null;
		if (presenceBoardUser.projectTimeBillable) {
			taskColor = "billable";
		} else if (presenceBoardUser.projectTimeBillable !== null && !presenceBoardUser.projectTimeBillable) {
			taskColor = "not-billable";
		}

		let categoryDisplay: string;
		let categoryDisplayExtraInfo: string;
		if (presenceBoardUser.paused) {
			categoryDisplay = `${messages["presenceBoard.category.break"]}`;
			categoryDisplayExtraInfo = presenceBoardUser.workingTimeTypeSubCategory ? `(${messages["presenceBoard.category.active." + presenceBoardUser.workingTimeTypeSubCategory]})` : "";
		} else {
			categoryDisplay = messages["presenceBoard.category.active." + presenceBoardUser.workingTimeTypeSubCategory];
		}

		return {
			fullName: presenceBoardUser.fullName,
			categoryDisplay: categoryDisplay,
			categoryDisplayExtraInfo: categoryDisplayExtraInfo,
			assignedGroups: presenceBoardUser.groups,
			colorType: color,
			icon: presenceBoardUser.paused ? PresenceBoardHelper.ICON_PAUSED : PresenceBoardHelper.ICON_RECORDING,
			taskName: presenceBoardUser.taskName,
			taskPath: presenceBoardUser.taskBreadcrumbs && presenceBoardUser.taskName && breadcrumbsWithoutName(presenceBoardUser.taskBreadcrumbs, presenceBoardUser.taskName),
			taskColor: taskColor
		};
	}

	private getInactiveUserConfig(presenceBoardUser: InactiveUser): PresenceBoardCardConfig {
		return {
			fullName: presenceBoardUser.fullName,
			categoryDisplay: presenceBoardUser.wasActiveToday ? messages["presenceBoard.category.inactive"] : messages["presenceBoard.category.notActiveYet"],
			assignedGroups: presenceBoardUser.groups,
			colorType: presenceBoardUser.wasActiveToday ? "inactive" : "not-active-yet"
		};
	}

	private getAbsentUserConfig(presenceBoardUser: AbsentUser): PresenceBoardCardConfig {
		const end = parseISODate(presenceBoardUser.end);
		const substitute = presenceBoardUser.substitute ? messages.withArguments("presenceBoard.substitute", presenceBoardUser.substitute.fullname) : "";
		const backText = `${messages["presenceBoard.back"]} ${formatDateDay(end)}, ${formatDate(end)}`;
		return {
			fullName: presenceBoardUser.fullName,
			categoryDisplay: presenceBoardUser.end ? `${backText}` : messages["presenceBoard.category.absent"],
			assignedGroups: presenceBoardUser.groups,
			colorType: "absent",
			extraInfo: substitute
		};
	}

	private getHolidayUserConfig(presenceBoardUser: HolidayUser): PresenceBoardCardConfig {
		return {
			fullName: presenceBoardUser.fullName,
			categoryDisplay: `${messages["presenceBoard.category.holiday"]} (${presenceBoardUser.holidayDescription})`,
			assignedGroups: presenceBoardUser.groups,
			colorType: "absent"
		};
	}

	private getNoWorkDayUserConfig(presenceBoardUser: NoWorkDayUser): PresenceBoardCardConfig {
		const decodedMessages = presenceBoardUser.workingDays.map(workingDay => messages[`workScheduleModel.${workingDay.toLowerCase()}.short`]);
		return {
			fullName: presenceBoardUser.fullName,
			categoryDisplay: `${messages["presenceBoard.category.noWorkDay"]} ${decodedMessages.join(", ")}`,
			assignedGroups: presenceBoardUser.groups,
			colorType: "absent"
		};
	}

	/* REDUCED CARD */

	public getReducedPresenceBoardUserCard(config: PresenceBoardCardConfig) {
		const card = $("#cloneCardReduced")
			.clone(false)
			.removeAttr("id")
			.show();

		card.find("[data-category]").text(config.categoryDisplay);

		// colour is owned by CSS (components/presenceBoard.less), so the card follows the theme
		card.find("[data-card-body]").attr("data-presence-state", config.colorType ?? null);
		card.find("[data-name]").text(config.fullName).prop("title", config.fullName);

		card.find("[data-icon]").children().hide();
		if (config.icon !== "") {
			card.find(`[data-${config.icon}]`).show();
		}

		return card;
	}

	/* FULL CARD */

	// tslint:disable-next-line:max-line-length
	public getPresenceBoardUserCard(config: PresenceBoardCardConfig) {
		const card = $("#cloneCardFull")
			.clone(false)
			.removeAttr("id")
			.show();

		card.find("[data-category]").text(config.categoryDisplay);
		card.find("[data-break-info]").text(config.categoryDisplayExtraInfo);

		// colour is owned by CSS (components/presenceBoard.less), so the card follows the theme
		card.find("[data-card-body]").attr("data-presence-state", config.colorType ?? null);
		card.find("[data-name]").text(config.fullName).prop("title", config.fullName);
		card.find("[data-assigned-groups]").html(htmlEscape(config.assignedGroups) || PresenceBoardHelper.EMPTY_LINE).prop("title", config.assignedGroups);

		card.find("[data-icon]").children().hide();
		if (config.icon !== "") {
			card.find(`[data-${config.icon}]`).show();
		}

		card.find("[data-extra-info]").html(htmlEscape(config.extraInfo) || PresenceBoardHelper.EMPTY_LINE).prop("title", config.extraInfo);

		const taskView = card.find("[data-task]");
		if (this.config.displayRunningProjectTime) {
			const taskNameEscaped = htmlEscape(config.taskName);
			if (config.taskName && config.taskPath) {
				taskView
					.addClass(config.taskColor || "neutral")
					.html(`
						<div class="text-nowrap" title="${taskNameEscaped}">${taskNameEscaped}</div>
						<div class="text-nowrap text-task-path" title="${config.taskPath}">${config.taskPath}</div>
					`);
			} else if (config.taskName) {
				taskView
					.addClass(config.taskColor)
					.html(`
						<div class="text-nowrap text-task-name" title="${taskNameEscaped}">${taskNameEscaped}</div>
					`);
			} else {
				// No project time available
				taskView.addClass(config.taskColor);
			}
		} else {
			taskView.hide();
		}
		return card;
	}
}
